import { toast } from 'react-toastify';
import { MdOutlineCompareArrows } from 'react-icons/md';
import { Button, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { useCurrentUser } from '@lp/core/hooks/useCurrentUser';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { StatusGroup, useCasesBoardStore } from '@/store/cases-board.store';
import { boardStatusList } from '@/constants/case/case.constants';
import { SavedGroup } from './SavedGroup';
import { EditGroup } from './EditGroup';
import { useStatusGroups } from '../hooks/useStatusGroups';
import { AvailableStatus } from './AvailableStatus';
import { CaseBoardPermissions } from '@/permissions';
import { UserPreferences } from '@/api/apis/user';

export const CustomGroupsModal = ({ onOpenChange }: { onOpenChange: () => void }) => {
  const groupsOrdered = useCasesBoardStore((store) => store.groupsOrdered);
  const setBoardPreferences = useCasesBoardStore((store) => store.setBoardPreferences);
  const { updateUserPreferences } = useCurrentUser();

  const setGroups = async (groups: StatusGroup[]) => {
    try {
      const showGroups = groups.length > 0;
      const payload = [
        { name: UserPreferences.groupsOrdered, value: JSON.stringify(groups) },
        { name: UserPreferences.showGroups, value: JSON.stringify(showGroups) },
      ];

      setBoardPreferences({ groupsOrdered: groups, showGroups });

      await updateUserPreferences(payload);
      toast.success('Groups have been saved successfully');
    } catch {
      toast.error('An error occurred while saving groups');
    }
  };

  const {
    addStatus,
    removeStatus,
    createGroup,
    editGroup,
    onGroupStatusDelete,
    onGroupDelete,
    onStartGroupEdit,
    setGroupName,
    groups,
    editingGroupId,
    selectedStatuses,
    groupName,
  } = useStatusGroups({ initialGroups: groupsOrdered });
  const { checkPermission } = useContext(AuthContext);
  const canSaveCustomGroups = checkPermission(CaseBoardPermissions.CaseBoardUpdate);

  const saveGroups = (onClose: () => void) => {
    setGroups(groups);
    onClose();
  };

  const availableStatuses = boardStatusList.filter(
    (status) => !groups.some((g) => g.statuses.includes(status) && g.id !== editingGroupId),
  );

  return (
    <Modal
      isOpen
      onOpenChange={onOpenChange}
      size="3xl"
      classNames={{
        footer: 'flex flex-col gap-4',
        base: 'max-h-screen m-0!',
        body: 'overflow-auto',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <h3 className="text-hl text-base-20">Create Custom Group</h3>{' '}
            </ModalHeader>
            <ModalBody>
              <div className="flex gap-4 text-m-m">
                <div className="flex flex-col flex-1 gap-6">
                  <div>
                    <div className="text-l-sb mb-3">Custom groups</div>
                    {groups.length ? (
                      <div className="flex flex-col gap-3">
                        {groups.map((g) => (
                          <SavedGroup
                            key={g.id}
                            id={g.id}
                            name={g.name}
                            statuses={g.statuses}
                            onDelete={onGroupDelete}
                            onStatusDelete={onGroupStatusDelete}
                            onEdit={onStartGroupEdit}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-m-r text-base-30">No groups have been created</div>
                    )}
                  </div>
                  <Divider />
                  <div className="flex flex-col flex-1 max-h-100 overflow-y-auto gap-2 pr-2">
                    <div className="text-l-sb mb-1">Available Statuses</div>
                    {availableStatuses.map((status) => (
                      <AvailableStatus
                        key={status}
                        status={status}
                        disabled={selectedStatuses.includes(status)}
                        onClick={addStatus}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <Divider orientation="vertical" className="flex-1" />
                  <MdOutlineCompareArrows size={24} color="#D8D9E0" />
                  <Divider orientation="vertical" className="flex-1" />
                </div>
                <div className="flex-1">
                  <EditGroup
                    title={editingGroupId ? 'Edit Group' : 'New Group'}
                    saveBtnText={editingGroupId ? 'Save Changes' : 'Create Group'}
                    groupName={groupName}
                    selectedStatuses={selectedStatuses}
                    setGroupName={setGroupName}
                    onSave={editingGroupId ? editGroup : createGroup}
                    onStatusRemove={removeStatus}
                  />
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Divider />
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onPress={onClose}>
                  Discard
                </Button>
                <Button isDisabled={!canSaveCustomGroups} variant="solid" onPress={() => saveGroups(onClose)}>
                  Save
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
